import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { HexColorPicker } from 'react-colorful';
import { RootReducer } from 'redux/reducers';
import { selectActiveDesignSystem, selectActiveDsChat } from 'redux/db/reducers';
import { selectActiveDsTab } from 'redux/mode/reducers';
import { showConfirmModal } from 'redux/mode/actions';
import useScrollMemory from 'hooks/useScrollMemory';
import { useDsChatQueue } from 'hooks/useDsChatQueue';
import {
	ChatMode,
	DsMode,
	IconPackId,
	UserChatEntry,
} from 'dragonmouse-shared/interfaces';
import { ICON_PACKS } from 'dragonmouse-shared/constants';
import IconPackSelector from 'components/Widget/Input/IconPackSelector';
import MarkdownText from 'components/Widget/MarkdownText';
import forge from 'apis/forge';
import { toast } from 'react-toastify';
import { DS_EDITOR_PREVIEW_BG_DEFAULT } from 'tokens';
import * as Base from '../styles';
import * as S from './styles';

// Inline mode row with label, default badge, and color picker
const ModeRowItem = ({
	mode: t,
	onLabelChange,
	onBgChange,
}: {
	mode: DsMode;
	onLabelChange: (modeId: string, newLabel: string) => void;
	onBgChange: (modeId: string, newBg: string) => void;
}) => {
	const [isColorOpen, setIsColorOpen] = useState(false);
	const [localLabel, setLocalLabel] = useState(t.label);
	const [bgDraft, setBgDraft] = useState(t.bg ?? DS_EDITOR_PREVIEW_BG_DEFAULT);
	const popoverRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setLocalLabel(t.label);
	}, [t.label]);

	useEffect(() => {
		setBgDraft(t.bg ?? DS_EDITOR_PREVIEW_BG_DEFAULT);
	}, [t.bg]);

	// Commit draft color when popover closes
	useEffect(() => {
		if (!isColorOpen && bgDraft !== (t.bg ?? DS_EDITOR_PREVIEW_BG_DEFAULT)) {
			onBgChange(t.id, bgDraft);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- commit only when popover closes
	}, [isColorOpen]);

	// Close color popover on outside click
	useEffect(() => {
		const onMouseDown = (e: MouseEvent) => {
			if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
				setIsColorOpen(false);
			}
		};
		if (isColorOpen) {
			document.addEventListener('mousedown', onMouseDown);
			return () => document.removeEventListener('mousedown', onMouseDown);
		}
	}, [isColorOpen]);

	return (
		<S.ModeRow>
			<S.ModeColorPicker ref={popoverRef}>
				<S.ModeSwatch $color={bgDraft} onClick={() => setIsColorOpen((prev) => !prev)} />
				{isColorOpen && (
					<S.ModeColorPopover>
						<HexColorPicker color={bgDraft} onChange={setBgDraft} />
						<S.ModeColorHexInput
							value={bgDraft}
							onChange={(e) => setBgDraft(e.target.value)}
							onBlur={() => onBgChange(t.id, bgDraft)}
						/>
					</S.ModeColorPopover>
				)}
			</S.ModeColorPicker>
			<S.ModeLabelInput
				value={localLabel}
				onChange={(e) => setLocalLabel(e.target.value)}
				onBlur={(e) => onLabelChange(t.id, e.target.value)}
			/>
			{t.default && <S.ModeDefaultBadge>default</S.ModeDefaultBadge>}
		</S.ModeRow>
	);
};

const FoundationsView = () => {
	const dispatch = useDispatch();
	const { designSystemContent, activeDesignSystem, activeTab, dsChat } = useSelector(
		(r: RootReducer) => ({
			designSystemContent: r.mode.designSystemContent,
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			activeTab: selectActiveDsTab(r.mode),
			dsChat: selectActiveDsChat(r.db, r.mode.activeDsChatId),
		}),
		shallowEqual
	);
	const scrollRef = useScrollMemory(activeTab?.id ?? null);
	const { iconPack: reduxIconPack, display } = designSystemContent?.config ?? {};
	const modes = display?.modes;
	const overviewFile = designSystemContent?.files?.find((f) => f.path === 'config/overview.md');
	const overviewContent = overviewFile?.content?.replace(/^#\s+.+\n*/, '').trim();

	const queue = useDsChatQueue(dsChat?.id ?? null);
	const iconPackReconcileKey = 'foundations:reconcile-iconpack';
	const isIconPackReconcileQueued = queue.items.some((e) => e.clientKey === iconPackReconcileKey);

	const updateIconPack = async (newPack: IconPackId) => {
		if (!activeDesignSystem?.id) return;
		await forge.updateDsFiles({
			designSystemId: activeDesignSystem.id,
			files: [{ path: 'config/icons.json', content: JSON.stringify({ pack: newPack }) }],
		});
	};

	const reconcileIconPack = (oldPack: IconPackId, newPack: IconPackId) => {
		if (!dsChat?.id) {
			toast.error('Cannot reconcile: no active chat');
			return;
		}
		if (isIconPackReconcileQueued) return;
		const message = [
			`The icon pack was changed from ${ICON_PACKS[oldPack].name} to ${ICON_PACKS[newPack].name}.`,
			"Please update all component icon references to use the new pack's syntax and icon names.",
		].join(' ');

		const entry: UserChatEntry = {
			kind: 'user',
			message,
			attachments: [],
			sourceFrameIds: [],
		};
		queue.enqueue({
			label: 'Reconcile icon pack',
			clientKey: iconPackReconcileKey,
			payload: { kind: 'addUserEntry', entry, chatMode: ChatMode.DESIGN_AND_CHAT },
		});
	};

	const onIconPackChange = (newPack: IconPackId) => {
		if (!activeDesignSystem?.id || newPack === reduxIconPack) return;
		const oldPack = reduxIconPack ?? 'phosphor';
		const hasDsChat = !!dsChat?.id;

		dispatch(
			showConfirmModal({
				title: 'Change Icon Pack',
				description: `Switching to ${
					ICON_PACKS[newPack].name
				} will require updating icon references in your components.${
					hasDsChat ? ' Reconcile uses chat to update the entire DS to match the new icon pack' : ''
				}`,
				submitLabel: hasDsChat ? 'Change & Reconcile' : 'Change',
				altActionLabel: hasDsChat ? 'Change Only' : undefined,
				cancelLabel: 'Cancel',
				onSubmit: hasDsChat
					? async () => {
							try {
								await updateIconPack(newPack);
							} catch (err) {
								console.error('Failed to change icon pack:', err);
								toast.error('Failed to change icon pack');
								return;
							}
							try {
								await reconcileIconPack(oldPack, newPack);
							} catch (err) {
								console.error('Failed to reconcile icons:', err);
								toast.error('Icon pack updated but failed to reconcile icons');
							}
					  }
					: async () => {
							try {
								await updateIconPack(newPack);
							} catch (err) {
								console.error('Failed to change icon pack:', err);
								toast.error('Failed to change icon pack');
							}
					  },
				onAltAction: hasDsChat
					? async () => {
							try {
								await updateIconPack(newPack);
							} catch (err) {
								console.error('Failed to change icon pack:', err);
								toast.error('Failed to change icon pack');
							}
					  }
					: undefined,
			})
		);
	};

	const updateDisplayJson = async (merge: (existing: any) => any) => {
		if (!activeDesignSystem?.id) return;

		const displayFile = designSystemContent?.files?.find((f) => f.path === 'config/display.json');
		let existing: any = {};
		if (displayFile?.content) {
			try {
				existing = JSON.parse(displayFile.content);
			} catch (parseErr) {
				console.warn(
					'Failed to parse existing display.json, starting with empty object:',
					parseErr
				);
			}
		}

		const merged = merge(existing);

		try {
			await forge.updateDsFiles({
				designSystemId: activeDesignSystem.id,
				files: [{ path: 'config/display.json', content: JSON.stringify(merged) }],
			});
		} catch (err) {
			console.error('Failed to update display config:', err);
			toast.error('Failed to update display config');
		}
	};

	const onModeLabelChange = (modeId: string, newLabel: string) => {
		updateDisplayJson((existing) => ({
			...existing,
			modes: (existing.modes ?? []).map((t: any) =>
				t.id === modeId ? { ...t, label: newLabel } : t
			),
		}));
	};

	const onModeBgChange = (modeId: string, newBg: string) => {
		updateDisplayJson((existing) => ({
			...existing,
			modes: (existing.modes ?? []).map((t: any) => (t.id === modeId ? { ...t, bg: newBg } : t)),
		}));
	};

	return (
		<Base.SectionBase ref={scrollRef}>
			<Base.SectionHeader>
				<Base.SectionTitle>Foundations</Base.SectionTitle>
			</Base.SectionHeader>
			<Base.SectionContent>
				<S.CardsContainer>
					{modes && modes.length > 0 && (
						<S.FoundationCard>
							<S.CardTitle>Modes</S.CardTitle>
							<S.CardDescription>
								Configure the user-facing names and preview background colors for each mode. To add
								or remove modes, or change the default mode, ask chat.
							</S.CardDescription>
							{modes.map((t) => (
								<ModeRowItem
									key={t.id}
									mode={t}
									onLabelChange={onModeLabelChange}
									onBgChange={onModeBgChange}
								/>
							))}
						</S.FoundationCard>
					)}
					<S.FoundationCard>
						<S.CardTitle>Icon Pack</S.CardTitle>
						<S.CardDescription>Choose the icon library for your design system.</S.CardDescription>
						{reduxIconPack && (
							<IconPackSelector value={reduxIconPack} onChange={onIconPackChange} />
						)}
					</S.FoundationCard>
					{overviewContent && (
						<S.FoundationCard>
							<S.CardTitle>Overview</S.CardTitle>
							<S.OverviewBody>
								<MarkdownText>{overviewContent}</MarkdownText>
							</S.OverviewBody>
						</S.FoundationCard>
					)}
				</S.CardsContainer>
			</Base.SectionContent>
		</Base.SectionBase>
	);
};

export default FoundationsView;
